// src/pages/info/Popupnotice.jsx

import { useState, useEffect, useCallback } from "react";

const SHEET_ID  = "1MVmmzs6rULqdqq2kgGlewrlSVnfMOYvbXs9dU83-jIo";
const SHEET_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=popup`;

// Try multiple URL formats for Google Drive images
function toDriveEmbed(url) {
  if (!url) return url;

  // Extract file ID from any Drive URL format
  let fileId = null;

  const ucMatch  = url.match(/[?&]id=([\w-]+)/);
  const fileMatch = url.match(/\/file\/d\/([\w-]+)/);

  if (ucMatch)   fileId = ucMatch[1];
  else if (fileMatch) fileId = fileMatch[1];

  if (fileId) {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1600-h900`;
  }

  // Not a Drive URL — return as-is
  return url;
}

if (!document.getElementById("ssc-kf")) {
  const s = document.createElement("style");
  s.id = "ssc-kf";
  s.textContent = `
    @keyframes sscOverlayIn { from{opacity:0} to{opacity:1} }
    @keyframes sscCardIn    { from{opacity:0;transform:translateY(28px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
    @keyframes sscSpin      { to{transform:rotate(360deg)} }
    @keyframes sscDotBlink  { 0%,80%,100%{opacity:0} 40%{opacity:1} }
  `;
  document.head.appendChild(s);
}

export default function PopupNotice() {
  const [phase,      setPhase]     = useState("loading");
  const [images,     setImages]    = useState([]); // [{url, caption}]
  const [current,    setCurrent]   = useState(0);
  const [imgLoaded,  setImgLoaded] = useState(false);
  const [open,       setOpen]      = useState(true);
  const [closing,    setClosing]   = useState(false);

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(() => setOpen(false), 280);
  }, []);

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [close]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        console.log("[PopupNotice] Fetching sheet...");
        const res = await fetch(SHEET_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const text = await res.text();
        const match = text.match(/setResponse\(([\s\S]*?)\);/);
        if (!match) throw new Error("Bad response format");

        const json = JSON.parse(match[1]);
        const rows = json?.table?.rows;

        console.log("[PopupNotice] Total rows (inc header):", rows?.length);

        // Skip header row (rows[0] = "image_url","caption","show")
        // Only include rows where column E (index 4) = "yes"
        const dataRows = (rows || []).slice(1).filter(r => {
          const hasUrl  = r.c?.[0]?.v;
          const showVal = r.c?.[4]?.v ? String(r.c[4].v).trim().toLowerCase() : "no";
          return hasUrl && showVal === "yes";
        });

        if (dataRows.length === 0) throw new Error("No rows with show=yes found in popup sheet");

        const parsed = dataRows.map(r => ({
          url:     toDriveEmbed(String(r.c[0].v).trim()),
          caption: r.c[1]?.v ? String(r.c[1].v).trim() : "",
        }));

        console.log("[PopupNotice] Images found:", parsed.length, parsed.map(p => p.url));

        if (!cancelled) { setImages(parsed); setPhase("ready"); }
      } catch (err) {
        console.error("[PopupNotice] ❌", err.message);
        if (!cancelled) setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset imgLoaded when switching images
  useEffect(() => { setImgLoaded(false); }, [current]);

  if (!open) return null;

  const img     = images[current];
  const total   = images.length;
  const hasMany = total > 1;

  const overlayAnim = closing
    ? { opacity: 0, transition: "opacity 0.28s ease", animation: "none" }
    : { animation: "sscOverlayIn 0.35s ease forwards" };

  const cardAnim = closing
    ? { opacity: 0, transform: "translateY(14px) scale(0.98)", transition: "opacity 0.28s ease, transform 0.28s ease", animation: "none" }
    : { animation: "sscCardIn 0.45s cubic-bezier(.22,1,.36,1) forwards" };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
        background: "rgba(20,45,40,0.75)",
        overflowY: "auto",   // ← overlay scrolls if card is taller than screen
        ...overlayAnim,
      }}
    >
      <div style={{
        background: "#fff", borderRadius: 3, overflow: "hidden",
        maxWidth: 580, width: "100%",
        margin: "auto",      // ← keeps card centred when scrolling
        boxShadow: "0 4px 6px rgba(0,0,0,0.08), 0 24px 64px rgba(0,0,0,0.4)",
        ...cardAnim,
      }}>

        {/* Teal top stripe */}
        <div style={{ height: 5, background: "#2d6b5e" }} />

        {/* Header */}
        <div style={{
          padding: "15px 18px 13px", borderBottom: "1px solid #ececec",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="26" height="30" viewBox="0 0 26 30" fill="none">
              <rect x="10" y="0" width="6" height="8" rx="1" fill="#c8922a"/>
              <rect x="12.5" y="2" width="1.5" height="6" fill="#fff"/>
              <path d="M2 13 Q2 5 13 5 Q24 5 24 13 L24 30 H2 Z" fill="#c8922a"/>
              <rect x="5" y="17" width="5" height="13" rx="1" fill="#fff"/>
              <rect x="16" y="17" width="5" height="13" rx="1" fill="#fff"/>
              <path d="M9 13 Q9 9 13 9 Q17 9 17 13 L17 17 H9 Z" fill="#fff"/>
            </svg>
            <div>
              <p style={{ margin: 0, fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", color: "#7a2020", fontWeight: "bold", fontFamily: "Georgia,serif" }}>
                Saint Stephen's Church
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 10, letterSpacing: "1.5px", textTransform: "uppercase", color: "#aaa" }}>
                Parish Notice {hasMany ? `· ${current + 1} of ${total}` : ""}
              </p>
            </div>
          </div>

          <button
            onClick={close} aria-label="Close"
            style={{ width: 30, height: 30, borderRadius: "50%", border: "2px solid #ddd", background: "transparent", color: "#888", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, { borderColor: "#7a2020", color: "#7a2020", background: "#fff5f5" })}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, { borderColor: "#ddd", color: "#888", background: "transparent" })}
          >✕</button>
        </div>

        {/* Image area */}
        <div style={{ background: "#1a2e2a", minHeight: 240, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>

          {/* Spinner */}
          {(phase === "loading" || (phase === "ready" && !imgLoaded)) && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, border: "3px solid rgba(200,146,42,0.2)", borderTopColor: "#c8922a", borderRadius: "50%", animation: "sscSpin 0.75s linear infinite" }} />
              <p style={{ margin: 0, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: "rgba(200,146,42,0.65)" }}>Loading…</p>
            </div>
          )}

          {/* Error */}
          {phase === "error" && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24, textAlign: "center" }}>
              <span style={{ fontSize: 30 }}>🕊️</span>
              <p style={{ margin: 0, fontSize: 13, color: "#ccc", lineHeight: 1.6 }}>
                No notice published yet.<br />
                <span style={{ color: "#c8922a", fontSize: 12 }}> <strong></strong><em></em>...</span>
              </p>
            </div>
          )}

          {/* Image */}
          {phase === "ready" && img && (
            <img
              key={img.url}
              src={img.url}
              alt="Parish Notice"
              onLoad={() => { console.log("[PopupNotice] ✅ Image loaded!"); setImgLoaded(true); }}
              onError={(e) => { console.error("[PopupNotice] ❌ Image failed:", e.target.src); e.target.style.display="none"; setImgLoaded(true); }}
              style={{ width: "100%", maxHeight: 440, objectFit: "contain", display: "block", opacity: imgLoaded ? 1 : 0, transition: "opacity 0.5s ease" }}
            />
          )}

          {/* Prev / Next arrows for multiple images */}
          {phase === "ready" && hasMany && imgLoaded && (
            <>
              <button
                onClick={() => setCurrent(c => (c - 1 + total) % total)}
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", color: "#fff", width: 36, height: 36, borderRadius: "50%", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >‹</button>
              <button
                onClick={() => setCurrent(c => (c + 1) % total)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", color: "#fff", width: 36, height: 36, borderRadius: "50%", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >›</button>
            </>
          )}
        </div>

        {/* Dot indicators for multiple images */}
        {phase === "ready" && hasMany && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, padding: "8px 0", background: "#1a2e2a" }}>
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                style={{ width: i === current ? 18 : 8, height: 8, borderRadius: 4, border: "none", cursor: "pointer", background: i === current ? "#c8922a" : "rgba(255,255,255,0.35)", padding: 0, transition: "all 0.2s" }}
              />
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "11px 18px", borderTop: "1px solid #ececec", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "#fafafa" }}>
          <p style={{ margin: 0, fontSize: 12, color: "#777", fontStyle: "italic", fontFamily: "Georgia,serif" }}>
            {img?.caption || "One Lord · One Community"}
          </p>
          <button
            onClick={close}
            style={{ flexShrink: 0, background: "#7a2020", color: "#fff", border: "none", padding: "8px 20px", fontSize: 11, fontWeight: "bold", letterSpacing: "1.5px", textTransform: "uppercase", cursor: "pointer", borderRadius: 2 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#2d6b5e"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#7a2020"; }}
          >Close</button>
        </div>

        {/* Gradient bottom stripe */}
        <div style={{ height: 3, background: "linear-gradient(to right, #2d6b5e, #c8922a)" }} />
      </div>
    </div>
  );
}